import { Badge } from "@babackapp/customer-ui/components/badge";
import { CustomerUiProvider } from "@babackapp/customer-ui/components/provider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { createStore, Provider } from "jotai";
import { AlertTriangleIcon, CrownIcon, InfoIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Component, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useLocation, useNavigate, useOutlet } from "react-router-dom";

const widgetStore = createStore();

export function ReturnWidgetLayout() {
  const segment = useLocation().pathname.split("/")[3];
  const outlet = useOutlet();

  return (
    <Provider store={widgetStore}>
      <TooltipProvider>
        <CustomerUiProvider className="h-screen w-screen bg-white flex flex-col overflow-hidden">
          <div className="relative flex-1 min-h-0 flex flex-col">
            {/*<AdminBadge />*/}
            <div className="flex-1 min-h-0 z-0 relative overflow-hidden flex flex-col p-8">
              <WidgetErrorBoundary>
                <Suspense>
                  <AnimatePresence mode="wait" propagate>
                    <motion.div key={segment} className="flex-1 min-h-0 w-full flex flex-col">
                      {outlet}
                    </motion.div>
                  </AnimatePresence>
                </Suspense>
              </WidgetErrorBoundary>
            </div>
          </div>
        </CustomerUiProvider>
      </TooltipProvider>
    </Provider>
  );
}

type ErrorBoundaryState = { error: Error | null };

class WidgetErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      "[ReturnWidget] Error boundary caught:",
      error,
      info.componentStack,
    );
  }

  render() {
    if (this.state.error) {
      return (
        <WidgetErrorFallback
          error={this.state.error}
          onReset={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function WidgetErrorFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
}) {
  const navigate = useNavigate();

  const handleReset = () => {
    onReset();
    navigate(-1);
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
      <AlertTriangleIcon className="size-10 text-destructive" />
      <div className="flex flex-col gap-1">
        <p className="font-sans text-sm font-medium">Une erreur est survenue</p>
        <p className="font-content text-xs text-muted-foreground">
          {error.message}
        </p>
      </div>
      <button
        onClick={handleReset}
        className="font-content text-sm text-primary underline underline-offset-4"
      >
        Retour
      </button>
    </div>
  );
}

function AdminBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className="z-10 cursor-help bg-admin text-admin-foreground border-admin-border absolute -translate-y-1/2 top-0 left-0 right-0 mx-auto">
          <CrownIcon /> Admin <InfoIcon />
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        Vous voyez cette interface en mode administrateur
      </TooltipContent>
    </Tooltip>
  );
}
